import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { usePlannerLens } from "@/hooks/usePlannerLens";
import { readyBlockedCounts } from "@shared/executor/task-counts";
import type { Client } from "@shared/schema";
import {
  INTERVIEW_TOTAL_STEPS,
  absoluteStepIndex,
  chapterAt,
  nextPosition,
  previousPosition,
  screenAt,
  type InterviewPosition,
} from "@shared/interview/chapters";
import { PetsScreen } from "./PetsScreen";
import { WorkScreen } from "./WorkScreen";
import { ChildrenScreen } from "./ChildrenScreen";
import { DayToDayScreen } from "./DayToDayScreen";
import { YouScreen } from "./YouScreen";
import { ExecScreen } from "./ExecScreen";
import { BenefScreen } from "./BenefScreen";
import { IdsScreen } from "./IdsScreen";
import { CheckpointScreen } from "./CheckpointScreen";
import { FirearmsScreen, HomeAndPropertyScreen, SafesScreen, ValuablesScreen, VehiclesScreen } from "./ch2/Ch2Screens";
import { NoneAnswerProvider, prunedCounts, useNoneAnswers } from "./useNoneAnswer";
import { chunk, usePillLayout, usePillRefs } from "./StepPills";
import { ArrowDown, ArrowLeft } from "lucide-react";
import { HOME_BOARD } from "@shared/plan-home/home-board.generated";
import { useAnyPanelOpen } from "./useOnePanelOpen";

/** s233 (D-233.51): the home's name, board 12's word ("Home"). */
const HOME_WORD = HOME_BOARD.labels.home;


/**
 * INTERVIEW-SHELL-1 (s205, SHELL-PILOT-1) — the interview surface.
 *
 * A full-screen ladder rung with NO sidebar (`App.tsx` renders it instead of `AppShell`, not inside
 * it). It owns the header, the frame and the position; each screen owns only its own capture.
 *
 * ## The two numbers (D-156 / G7)
 *
 * `readyBlockedCounts(taskKeyUniverse, gaps)` — the ONE shared function the executor panel and the
 * server both call. `total` is read live from the lens payload, never a literal, so the opening number
 * is whatever the task registry actually holds (charter §5).
 *
 * ## The delta (charter §5, corrected s194)
 *
 * The framing keys on the TASK DELTA, never on whether the answer was affirmative — "is that debt
 * insured?" → Yes REMOVES a task. So the line below reads the sign of the change, nothing else.
 *
 * 🔴 COPY IS PLACEHOLDER except where a convention rules it (§4B). SPEC owns the wording (D-202.22).
 */
/**
 * s228 (PLAN-HOME-1) — two ways the plan's home mounts the shell, beside the full-screen rung it always was:
 *
 *  • `framed` — inside the home frame (board 12: header, Planner/Executor tabs). The frame carries Sign out and the
 *    executor-task counter on its Executor tab (rule 127(2): "no counter anywhere else"), so the shell drops its own
 *    strip and counter box, and reports its "none" answers into the FRAME's provider so the tab's counter prunes too.
 *  • `visit` — one screen opened from the sidebar or the home, with "Return to …" above it (board 12, :577-598).
 *    The position is LOCAL and never saved: browsing the plan must not move "where you left off".
 */
export type InterviewVisit = {
  /** Where the planner came from, as the link names it ("Home", "Settings"). */
  returnLabel: string;
  onReturn: () => void;
  /** s228 (item 6d): the visit moved to another screen — the page records it (the address), so a refresh stays. */
  onMove?: (position: InterviewPosition) => void;
};

export function InterviewShell(props: {
  clientData: Client;
  position: InterviewPosition;
  onSignOut?: () => void;
  framed?: boolean;
  visit?: InterviewVisit;
  /** s233 (D-233.51): "← Home" at the start of the top row, on every screen — the plan's home. */
  onHome?: () => void;
}) {
  // s221 Lane 1d: every screen's "none" tile reports into this provider; the body reads it. Framed, the frame owns it.
  if (props.framed) return <InterviewShellBody {...props} />;
  return (
    <NoneAnswerProvider>
      <InterviewShellBody {...props} />
    </NoneAnswerProvider>
  );
}

function InterviewShellBody({
  clientData,
  position: savedPosition,
  onSignOut,
  framed,
  visit,
  onHome,
}: {
  onSignOut?: () => void;
  clientData: Client;
  position: InterviewPosition;
  framed?: boolean;
  visit?: InterviewVisit;
  onHome?: () => void;
}) {
  const clientId = clientData.id;
  // s228: a visit moves a LOCAL cursor; the interview moves the saved one.
  const [visitPosition, setVisitPositionState] = useState(savedPosition);
  const setVisitPosition = useCallback((p: InterviewPosition) => { setVisitPositionState(p); visit?.onMove?.(p); }, [visit]);
  const position = visit ? visitPosition : savedPosition;
  const chapter = chapterAt(position);
  const screen = screenAt(position);

  const { data: lens } = usePlannerLens(clientId);
  const counts = useMemo(
    () => readyBlockedCounts(lens?.taskKeyUniverse ?? [], lens?.gaps ?? []),
    [lens?.taskKeyUniverse, lens?.gaps],
  );

  // Keep the previous pair; diff on refetch. The FIRST observation sets the baseline and shows
  // nothing — a delta line on arrival would be reporting a change the planner did not make.
  const prevCounts = useRef<{ total: number; ready: number } | null>(null);
  const [delta, setDelta] = useState<string | null>(null);
  useEffect(() => {
    if (!lens) return;
    const prev = prevCounts.current;
    prevCounts.current = { total: counts.total, ready: counts.ready };
    if (!prev) return;
    const dTotal = counts.total - prev.total;
    const dReady = counts.ready - prev.ready;
    if (dTotal < 0) {
      const n = -dTotal;
      setDelta(`That removed ${n} ${n === 1 ? "job" : "jobs"} from the list`);
    } else if (dTotal > 0) {
      // A rise is COVERAGE, not punishment (charter §5).
      setDelta(`${dTotal} more ${dTotal === 1 ? "job" : "jobs"} mapped`);
    } else if (dReady > 0) {
      setDelta(`${dReady} more now ready`);
    }
  }, [counts, lens]);

  const move = useMutation({
    mutationFn: async (next: InterviewPosition | null) => {
      if (next) {
        await apiRequest("PATCH", `/api/clients/${clientId}/interview-position`, { position: next });
      } else {
        // 🔴 PILOT ONLY. A real chapter never completes the interview — completion belongs after the
        // last chapter of six, at the Magic Moment. The pilot sets it so the flow has an exit while
        // five chapters are still SPEC-pending. Remove when chapter content lands.
        await apiRequest("PATCH", `/api/clients/${clientId}/interview-position`, { completed: true });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/clients"] }),
  });

  const goNext = useCallback(() => {
    const next = nextPosition(position);
    // s228: a visit that runs off the end of the interview goes back where it came from; it never completes it.
    if (visit) return next ? setVisitPosition(next) : visit.onReturn();
    move.mutate(next);
  }, [move, position, visit]);
  /** s221 Lane 1c, fix 8: a pill goes to its screen — any screen of the chapter, as the board allows. */
  const goTo = useCallback(
    (step: number) => {
      if (step === position.step) return;
      if (visit) setVisitPosition({ chapter: position.chapter, step });
      else move.mutate({ chapter: position.chapter, step });
    },
    [move, position, visit],
  );
  const goBack = useCallback(() => {
    const prev = previousPosition(position);
    if (!prev) return;
    if (visit) setVisitPosition(prev);
    else move.mutate(prev);
  }, [move, position, visit]);

  /** Refetch the lens after an answer lands, so the header numbers and the delta move. */
  const refreshCounts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "planner-lens"] });
  }, [clientId]);

  // s209 (board): "More below" shows while the page continues past the fold, and hides at the bottom.
  const [moreBelow, setMoreBelow] = useState(false);
  // s233 (D-233.54(2)): hidden while a card or panel is open, so it never covers one; back when it closes.
  const panelOpen = useAnyPanelOpen();
  useEffect(() => {
    const measure = () => {
      const doc = document.documentElement;
      setMoreBelow(doc.scrollHeight - window.scrollY - window.innerHeight > 24);
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(document.body);
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [position.chapter, position.step]);

  // s221 Lane 1d: a picked "none" tile drops the counter by its DECLARED prune, and shows its sentence.
  const none = useNoneAnswers();
  const shown = prunedCounts(counts, none.prune);

  // s228 (D-228.70): the step pills' layout — measured, so it runs before the early return below.
  const pillRefs = usePillRefs();
  const pillLayout = usePillLayout(pillRefs.row, pillRefs.pills, chapter?.screens.length ?? 0);

  if (!chapter || !screen) return null;

  const stepIndex = absoluteStepIndex(position);
  const pct = Math.round((stepIndex / Math.max(INTERVIEW_TOTAL_STEPS - 1, 1)) * 100);
  const canGoBack = !!previousPosition(position);

  return (
    // s221 Lane 1b (D-221.28): the board's look, scoped to THIS root. Every style below is an interview
    // style; nothing outside the shell changes until the rebrand.
    // s228: framed, the plan's frame already fills the window — a second full-screen minimum here would make every
    // page overflow by the frame's height, and "More below" would show on a page that does not continue.
    <div data-interview-theme className={`flex w-full flex-col bg-warm-cream font-sans text-warm-charcoal ${framed ? "" : "min-h-screen"}`}>
      {/* ── the top strip (Main.dc.html lines 26-32) ─────────────────────────────────────── */}
      {/* s226 (Reade: "1/ agree"): SIGN OUT at the right of the strip on every interview screen, styled as the
          board's Home link beside it (ghost, sage-800, 13px, 32px tall). The board's Home link is not built in the
          product; the strip carries Sign out alone. It signs out through the app's one logout (AuthContext), passed
          in by the page that mounts the shell, and lands on the sign-in page. */}
      {onSignOut && !framed && (
        <div className="flex min-h-[36px] items-center justify-end border-b border-warm-stone bg-warm-stone-light px-4 py-1.5 sm:px-10"
          data-testid="interview-strip">
          <button type="button" onClick={onSignOut}
            className="flex min-h-[32px] items-center text-[13px] text-sage-800 hover:underline"
            data-testid="interview-sign-out">
            Sign out
          </button>
        </div>
      )}
      {/* ── header ───────────────────────────────────────────────────────────────────── */}
      <header className="border-b border-warm-stone bg-warm-white">
        {/* the progress line */}
        <div className="h-[3px] w-full bg-warm-stone-light">
          <div
            className="h-[3px] bg-sage-600 transition-all duration-300"
            style={{ width: `${pct}%` }}
            data-testid="interview-progress"
            aria-hidden
          />
        </div>

        <div className="mx-auto w-full max-w-[1600px] px-4 py-3 sm:px-8">
          <div ref={pillRefs.row} className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="interview-step-row">
            {/* s228 (Reade, staging walk item 7): a visit goes back by a small "← Home" link at the start of the top
                row, drawn as board 1's Home link (Main.dc.html, the top strip: ghost, 13px, #2E4030, 32px tall) —
                not a boxed button above the title. */}
            {/* s233 (D-233.51, Reade: "yes I want a home link in each interview screen"): "← Home" on EVERY screen, drawn as
                the visit's link above (board 1 draws its Home link in a top strip the plan's frame has replaced, D-228.42 B).
                A visit that came from the home already reads "← Home", so there is never a second one. */}
            {onHome && visit?.returnLabel !== HOME_WORD && (
              <button type="button" onClick={onHome}
                className="mr-2 inline-flex min-h-8 items-center gap-1.5 text-[13px] text-sage-800 hover:underline"
                data-testid="interview-home">
                ← {HOME_WORD}
              </button>
            )}
            {visit && (
              <button type="button" onClick={visit.onReturn}
                className="mr-2 inline-flex min-h-8 items-center gap-1.5 text-[13px] text-sage-800 hover:underline"
                data-testid="interview-return">
                ← {visit.returnLabel}
              </button>
            )}
            <button
              type="button"
              onClick={goBack}
              disabled={!canGoBack || move.isPending}
              // s228 (D-228.70): the brand's thin sage focus ring, never the browser's black outline.
              className="inline-flex h-11 items-center gap-1.5 rounded-[8px] pr-1 text-[15px] text-warm-muted outline-none hover:text-warm-charcoal focus-visible:ring-1 focus-visible:ring-sage-600 disabled:opacity-40"
              data-testid="interview-back"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
            </button>

            <p className="mr-2 text-[13px] uppercase tracking-[0.08em] text-warm-muted" data-testid="interview-chapter-label">
              Chapter {chapter.number} · {chapter.name}
            </p>

            {/* step pills — done / current / upcoming (§4B 17), numbered as the board draws them */}
            {/* s228 (D-228.70, board v351): one line when there is room; balanced rows, no orphan, when not. */}
            <div ref={pillRefs.pills}
              className={pillLayout.inline ? "flex flex-wrap items-center gap-2" : "flex basis-full flex-col gap-2"}
              data-testid="interview-pills" data-rows={pillLayout.rows.join(",")}>
              {chunk(chapter.screens.map((s, i) => {
                const state = i < position.step ? "done" : i === position.step ? "current" : "upcoming";
                return (
                  <button
                    type="button"
                    key={s.key}
                    onClick={() => goTo(i)}
                    disabled={move.isPending}
                    aria-current={state === "current" ? "step" : undefined}
                    data-testid={`interview-pill-${s.key}`}
                    data-state={state}
                    data-step-pill
                    className={
                      // s228 (D-228.70, option 1): the board's pill (Main.dc.html:51-53) — 13px, padding 5px 12px 5px 6px,
                      // a 7px gap, 12px corners, 44px tall — narrower than the old 14px pill, so nine fit a wide row.
                      "inline-flex min-h-11 items-center gap-[7px] rounded-[12px] border py-[5px] pl-1.5 pr-3 text-[13px] disabled:cursor-wait " +
                      (state === "current"
                        ? "border-sage-600 bg-sage-600 text-warm-white"
                        : state === "done"
                          ? "border-warm-stone bg-warm-stone-light text-warm-charcoal hover:border-sage-600"
                          : "border-warm-stone bg-warm-white text-warm-muted hover:border-sage-600")
                    }
                  >
                    <span
                      className={
                        "flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full text-[11px] " +
                        (state === "current"
                          ? "bg-warm-white text-sage-800"
                          : state === "done"
                            ? "bg-sage-800 text-warm-white"
                            : "bg-warm-stone-light text-warm-muted")
                      }
                    >
                      {i + 1}
                    </span>
                    <span className="whitespace-nowrap">{s.crumb}</span>
                  </button>
                );
              }), pillLayout.inline ? [chapter.screens.length] : pillLayout.rows).map((row, r) =>
                pillLayout.inline ? row : <div key={r} className="flex items-center gap-2" data-testid={`interview-pill-row-${r}`}>{row}</div>)}
            </div>
          </div>
          {/* s221 Lane 1d: the board's none.delta sentence, under the pills (Main.dc.html `delta`). */}
          {none.deltas.map((d) => (
            <p key={d} className="mt-2 text-[13px] text-sage-600" data-testid="interview-none-delta">{d}</p>
          ))}
        </div>
      </header>

      {/* ── screen frame ─────────────────────────────────────────────────────────────── */}
      <main className="relative mx-auto w-full max-w-[1600px] flex-1 px-4 pb-16 pt-6 sm:px-8">
        {/* The executor-tasks box floats top-right, beside the title — it never pushes the title down.
            s228: framed, it lives on the Executor tab instead (rule 127(2)). */}
        {!framed && <div
          className="mb-4 ml-auto w-fit rounded-[12px] border border-sage-800 bg-warm-white px-5 py-2.5 text-center lg:absolute lg:right-8 lg:top-3 lg:mb-0"
          data-testid="interview-counts"
        >
          {/* s226 (board v334, rule 124): the board's label is 13px, 0.08em, warm.muted — #4A4640 since s233 (Main.dc.html line 62). */}
          <p className="text-[13px] uppercase tracking-[0.08em] text-warm-muted" data-testid="interview-counts-label">Executor tasks</p>
          <p className="mt-0.5 flex items-baseline justify-center gap-3 tabular-nums">
            <span>
              <span className="font-serif text-[30px] leading-none text-warm-charcoal" data-testid="interview-total">{shown.total}</span>{" "}
              <span className="text-[14px] text-warm-charcoal">tasks</span>
            </span>
            <span className="h-6 w-px bg-warm-stone" aria-hidden />
            <span>
              <span className="font-serif text-[30px] leading-none text-sage-600" data-testid="interview-ready">{shown.ready}</span>{" "}
              <span className="text-[14px] text-warm-charcoal">ready</span>
            </span>
          </p>
          {/* the delta line, sign-keyed on the task delta */}
          {delta && (
            <p className="mt-1 text-[12px] text-sage-600" data-testid="interview-delta">
              {delta}
            </p>
          )}
        </div>}

        <div className={framed ? undefined : "lg:pr-[260px]"}>
          {/* s221 Lane 1c, fix 3: the chapter opener line was the build's invention — it is in no canvas
              source. The screen title and the `why` line under it are the whole header. */}
          <h1 className="font-serif text-[30px] leading-tight text-warm-charcoal [text-wrap:pretty]" data-testid="interview-title">
            {screen.title}
          </h1>
          {screen.why && (
            <p className="mt-3 max-w-[1000px] text-[16px] leading-relaxed text-warm-muted [text-wrap:pretty]" data-testid="interview-why">
              {screen.why}
            </p>
          )}
        </div>

        <div className="mt-6">
          {screen.kind === "question" && screen.key === "you" && (
            <YouScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "kids" && (
            <ChildrenScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "daily" && (
            <DayToDayScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "pets" && (
            <PetsScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {/* s237 (chapter three PR 2): Work, the shared tile-to-row engine's first screen (D-236.61). */}
          {screen.kind === "question" && screen.key === "work" && (
            <WorkScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "exec" && (
            <ExecScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "benef" && (
            <BenefScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "ids" && (
            <IdsScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {/* s232 (chapter two, PR B): the four built screens of board 2. */}
          {/* s233 (D-233.81, board v440): Your home and Other property are one screen, "Home and property". */}
          {screen.kind === "question" && screen.key === "prop" && (
            <HomeAndPropertyScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "veh" && (
            <VehiclesScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "val" && (
            <ValuablesScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {/* s232 (chapter two, PR C): Firearms and Safes. */}
          {screen.kind === "question" && screen.key === "fa" && (
            <FirearmsScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "question" && screen.key === "safes" && (
            <SafesScreen clientId={clientId} onAnswered={refreshCounts} />
          )}
          {screen.kind === "checkpoint" && (
            <CheckpointScreen clientId={clientId} total={shown.total} ready={shown.ready} />
          )}
          {/* s232 (item 3): chapter two's unbuilt screens (Firearms, Safes, the close) show the board's title and why only. */}
          {screen.kind === "placeholder" && chapter.number !== 2 && (
            <p className="text-[15px] text-warm-muted" data-testid="interview-placeholder">
              [SPEC copy pending] — this chapter's screens are written once all six chapters are drawn.
            </p>
          )}
        </div>

        <div className="mt-8">
          {/* s223: the board's footer row — the button, and the step's `nextNote` beside it (Main.dc.html). */}
          <div className="flex flex-wrap items-center gap-5">
          <button
            type="button"
            onClick={goNext}
            disabled={move.isPending}
            className="h-12 rounded-[8px] bg-sage-600 px-8 text-[16px] text-warm-white hover:bg-sage-800 disabled:opacity-60"
            data-testid="interview-save-continue"
          >
            {move.isPending ? "One moment…" : "Save and continue"}
          </button>
          {screen.nextNote && (
            <span className="text-[13px] text-warm-muted" data-testid="interview-next-note">{screen.nextNote}</span>
          )}
          </div>
          {move.isError && (
            <p className="mt-2 text-[13px] text-destructive" data-testid="interview-move-error">
              That didn't save. Please try again.
            </p>
          )}
        </div>
      </main>

      {/* s209 (board): a long screen gives a sign there is more below. */}
      {moreBelow && !panelOpen && (
        <button
          type="button"
          onClick={() => window.scrollBy({ top: Math.round(window.innerHeight * 0.7), behavior: "smooth" })}
          className="fixed bottom-20 left-1/2 z-20 inline-flex h-12 -translate-x-1/2 items-center gap-2 rounded-full bg-sage-600 px-6 text-[16px] text-warm-white shadow-md hover:bg-sage-800"
          data-testid="interview-more-below"
        >
          More below <ArrowDown className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {/* ── the skip link: a CENTRED BAR at the very bottom (Reade, s204) ─────────────── */}
      <footer className="border-t border-warm-stone bg-warm-cream">
        <div className="mx-auto w-full max-w-[1600px] px-4 py-5 text-center sm:px-8">
          <button
            type="button"
            onClick={goNext}
            disabled={move.isPending}
            className="h-11 px-2 text-[15px] text-warm-charcoal hover:text-sage-800 disabled:opacity-40"
            data-testid="interview-skip"
          >
            Skip this screen
          </button>
        </div>
      </footer>
    </div>
  );
}
